import { Reservation } from "@/types/reservation";
import { Checkbox } from "@/components/ui/checkbox";
import React from "react";
import i18n from "@/i18n";
import {
  StatusCell,
  NameCell,
  GuestStatusCell,
  BookingNumberCell,
  RoomCell,
  ArrivalCell,
  DepartureCell,
  NotesCell,
  ActionsCell,
} from "../components/cells";
import { GuestsCell } from "../components/cells/Guests";
import { Column } from "@/components/ui/data-table";
import { calculateGuestsCount } from "@/lib/booking-utils";
import { formatRoomCell } from "./formatRoomCell";
import { ReservationBalanceCell } from "../components/cells/ReservationBalanceCell";

export const getReservationColumns = (
  selection: any,
  columnsVisible: {
    selection: boolean;
    status: boolean;
    room: boolean;
    actions: boolean;
  } = {
    selection: true,
    status: true,
    room: true,
    actions: true,
  },
): Column<Reservation>[] => [
  ...(columnsVisible.selection
    ? [
        {
          key: "select",
          header: (
            <div className="flex justify-center">
              <Checkbox
                checked={selection.isAllSelected}
                onCheckedChange={(state) => selection.selectAll(state === true)}
                aria-label={i18n.t(
                  "reservation.table.selectAll",
                  "Select all on this page",
                )}
              />
            </div>
          ),
          className: "text-center w-12",
          render: (_: unknown, reservation: Reservation) => (
            <div
              className="flex justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={selection.selectedReservations.includes(
                  reservation.id,
                )}
                onCheckedChange={(state) =>
                  selection.selectReservation(reservation.id, state === true)
                }
                aria-label={i18n.t(
                  "reservation.table.selectRow",
                  "Select reservation",
                )}
              />
            </div>
          ),
        },
      ]
    : []),
  ...(columnsVisible.status
    ? [
        {
          key: "status",
          header: "Status",
          className: "text-center",
          sortable: true,
          render: (_: unknown, reservation: Reservation) => (
            <StatusCell status={reservation.status} />
          ),
        },
      ]
    : []),
  {
    key: "guest",
    header: "Name",
    className: "text-center font-medium",
    sortable: true,
    render: (_, reservation) => (
      <NameCell
        language={reservation.guest.profile.language}
        firstName={reservation.guest.profile.firstName}
        lastName={reservation.guest.profile.lastName}
      />
    ),
  },
  {
    key: "guests",
    header: "Guests",
    className: "text-center",
    render: (_, reservation) => {
      const guestsCount = calculateGuestsCount(reservation.additionalGuests);
      return (
        <GuestsCell
          adultsCount={guestsCount.adults}
          childrenCount={guestsCount.children}
        />
      );
    },
  },
  {
    key: "guestStatus",
    header: i18n.t("reservation.table.guestStatus", "Guest status"),
    className: "text-center",
    render: (_, reservation) => (
      <GuestStatusCell
        preCheckinStatus={reservation.preCheckinStatus}
        checkinStatus={reservation.checkinStatus}
        preCheckoutStatus={reservation.preCheckoutStatus}
        checkoutStatus={reservation.checkoutStatus}
      />
    ),
  },
  {
    key: "bookingNumber",
    header: "Reservation number",
    className: "text-center",
    sortable: true,
    render: (_, reservation) => (
      <BookingNumberCell bookingNumber={reservation.bookingNumber} />
    ),
  },
  ...(columnsVisible.room
    ? [
        {
          key: "roomNumber",
          header: "Room",
          className: "text-center",
          sortable: true,
          render: (_: unknown, reservation: Reservation) => (
            <RoomCell {...formatRoomCell(reservation.room)} />
          ),
        },
      ]
    : []),
  {
    key: "arrivalDate",
    header: "Arrival",
    className: "text-center",
    sortable: true,
    render: (_, reservation) => (
      <ArrivalCell checkInDate={new Date(reservation.arrivalDate)} />
    ),
  },
  {
    key: "departureDate",
    header: "Departure",
    className: "text-center",
    sortable: true,
    render: (_, reservation) => (
      <DepartureCell checkOutDate={new Date(reservation.departureDate)} />
    ),
  },
  {
    key: "balance",
    header: "Balance",
    className: "text-center",
    render: (_, row) => (
      <ReservationBalanceCell
        reservationId={row.id}
        embeddedTransactions={row.transactions ?? []}
      />
    ),
  },
  {
    key: "notes",
    header: "Notes",
    className: "text-center",
    render: (_, reservation) => <NotesCell notes={reservation.notes} />,
  },
  ...(columnsVisible.actions
    ? [
        {
          key: "actions",
          header: "Actions",
          className: "text-center",
          render: (_: unknown, reservation: Reservation) => (
            <ActionsCell id={reservation.id} />
          ),
        },
      ]
    : []),
];
